import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  FirestoreError,
} from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { LeaderboardEntry, Course } from '@e-learning/shared/src/store/types';

/**
 * Fetch leaderboard entries
 */
export const fetchLeaderboardEntries = async (
  category?: string,
  difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'mastery'
): Promise<LeaderboardEntry[]> => {
  try {
    const resultsRef = collection(firestore, 'quizResults');
    let q = query(resultsRef, orderBy('score', 'desc'), orderBy('timestamp', 'desc'));
    
    // Apply filters if provided
    if (category) {
      q = query(q, where('courseCategory', '==', category));
    }
    
    if (difficulty) {
      q = query(q, where('difficulty', '==', difficulty));
    }
    
    const querySnapshot = await getDocs(q);
    
    // Get course details for each result
    const entries: LeaderboardEntry[] = [];
    const courseCache: Record<string, Course> = {};
    
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();
      const courseId = data.courseId;
      
      // Fetch course if not in cache
      if (!courseCache[courseId]) {
        const courseRef = doc(firestore, 'courses', courseId);
        const courseSnap = await getDoc(courseRef);
        if (courseSnap.exists()) {
          const courseData = courseSnap.data();
          courseCache[courseId] = {
            id: courseSnap.id,
            title: courseData.title || '',
            description: courseData.description || '',
            category: courseData.category || '',
          } as Course;
        }
      }
      
      const course = courseCache[courseId];
      if (course && data.userId) {
        // Only include authenticated users
        entries.push({
          userId: data.userId || '',
          userName: data.userName || 'Anonymous',
          userEmail: data.userEmail || '',
          courseId: courseId,
          courseTitle: course.title,
          category: course.category,
          difficulty: data.difficulty || 'intermediate',
          score: data.score || 0,
          totalQuestions: data.totalQuestions || 0,
          correctCount: data.correctCount || 0,
          timestamp: data.timestamp || '',
          quizId: data.quizId || '',
        } as LeaderboardEntry);
      }
    }
    
    return entries;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch leaderboard: ${firestoreError.message}`);
  }
};

/**
 * Get top performers
 */
export const getTopPerformers = async (
  limitCount: number = 10,
  category?: string,
  difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'mastery'
): Promise<LeaderboardEntry[]> => {
  try {
    const entries = await fetchLeaderboardEntries(category, difficulty);
    // Remove duplicates (keep best score per user per course)
    const uniqueEntries: Record<string, LeaderboardEntry> = {};
    
    entries.forEach((entry) => {
      const key = `${entry.userId}-${entry.courseId}-${entry.difficulty}`;
      if (!uniqueEntries[key] || uniqueEntries[key].score < entry.score) {
        uniqueEntries[key] = entry;
      }
    });
    
    return Object.values(uniqueEntries)
      .sort((a, b) => b.score - a.score)
      .slice(0, limitCount);
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to get top performers: ${firestoreError.message}`);
  }
};

